import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import LandingPage from './components/LandingPage';
import CompanySelection from './components/CompanySelection';
import SignIn from './components/SignIn';
import AdminDashboard from './components/AdminDashboard';
import CreateProject from './components/CreateProject';
import CreateUser from './components/CreateUser';
import ManagerView from './components/ManagerView';
import TeamMemberView from './components/TeamMemberView';
import PaymentPage from './components/PaymentPage';
import API_URL from './config';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentFailure from './components/PaymentFailure';
import SubscriptionExpired from './components/SubscriptionExpired';
import CustomerSupport from './components/CustomerSupport';
import FAQ from './components/FAQ';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';
import SiteFooter from './components/SiteFooter';
import { connectWebSocket, disconnectWebSocket } from "./websocket";
import { attachBrowserNotificationsToSocket } from "./components/browserNotifications";
import { useLanguage } from './context/LanguageContext';
import { installBrowserSecurityTracker } from './utils/browserSecurityTracker';
import { clearStoredAuth, getAuthHeaders, getLoginPath, getProfileCompanyId, getStoredAccessToken, getStoredCompanyId, getStoredRefreshToken, handleExpiredSession, normalizeAuthProfile, SESSION_EXPIRED_REASON, storeAuthSession } from './utils/authSession';
import { isLocalDevAuthEnabled } from './utils/localDevAuth';
import { installAxiosAuthInterceptors } from './utils/api';

installAxiosAuthInterceptors();

const withCompanyId = (WrappedComponent) => {
  return (props) => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const companyId = searchParams.get('companyId');
    return <WrappedComponent {...props} companyId={companyId} />;
  };
};

const SignInWithCompanyId = withCompanyId(SignIn);

const PublicLayout = () => (
  <>
    <Outlet />
    <SiteFooter />
  </>
);

const App = () => {
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [, setRefreshToken] = useState(null);
  const [, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState(getStoredCompanyId() || null);
  const hasRouteAuth = Boolean(token) || isLocalDevAuthEnabled();

  const wsConnected = useRef(false);
  const wsRef = useRef(null);
  const detachNotifRef = useRef(() => {});

  useEffect(() => installBrowserSecurityTracker(), []);

  useEffect(() => {
    const fetchUser = async () => {
      const storedToken = getStoredAccessToken();
      const storedUser = localStorage.getItem('user');
      const storedCompanyId = getStoredCompanyId();

      if (storedToken) {
        try {
          const response = await axios.get(`${API_URL}/api/profile/`, {
            headers: getAuthHeaders(storedToken),
          });
          const normalizedUser = normalizeAuthProfile(response.data, storedCompanyId);
          const profileCompanyId = getProfileCompanyId(normalizedUser, storedCompanyId);
          setUser(normalizedUser);
          setToken(storedToken);
          setRefreshToken(getStoredRefreshToken());
          setIsAuthenticated(true); // Set properly
          setCompanyId(profileCompanyId);
          storeAuthSession({
            accessToken: storedToken,
            refreshToken: getStoredRefreshToken(),
            user: normalizedUser,
            companyId: profileCompanyId,
          });
        } catch (error) {
          const redirectCompanyId = getStoredCompanyId() || '1';
          setUser(null);
          setToken(null);
          setRefreshToken(null);
          setIsAuthenticated(false);
          setCompanyId(getStoredCompanyId() || null);

          if (error.response?.status === 401) {
            const currentPath = window.location.pathname;
            const isAuthPage = currentPath === '/login' || currentPath === '/company-selection';
            if (isAuthPage) {
              clearStoredAuth({ preserveCompanyId: true });
            } else {
              handleExpiredSession({ companyId: redirectCompanyId });
              window.location.replace(getLoginPath({
                companyId: redirectCompanyId,
                reason: SESSION_EXPIRED_REASON,
              }));
            }
          } else {
            clearStoredAuth({ preserveCompanyId: true });
          }
        }
      } else {
        if (storedUser && isLocalDevAuthEnabled()) {
          const parsedUser = normalizeAuthProfile(JSON.parse(storedUser), getStoredCompanyId());
          if (parsedUser?.id) {
            setUser(parsedUser);
            setCompanyId(getProfileCompanyId(parsedUser, getStoredCompanyId()) || null);
            setIsAuthenticated(true);
            setLoading(false);
            return;
          }
        }

        if (storedUser) {
          clearStoredAuth();
        }
        setUser(null);
        setCompanyId(getStoredCompanyId() || null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (user && token) {
      if (!wsConnected.current) {
        wsRef.current = connectWebSocket(user);
        wsConnected.current = true;

        if (wsRef.current) {
          const { detach } = attachBrowserNotificationsToSocket(wsRef.current, {
            notifyWhenVisible: true,
            titleFor: (msg) => (msg?.type || "Notification").replace(/_/g, " "),
            bodyFor: (msg) => msg?.message || "",
            icon: "/icons/icon-192.png",
            throttleMs: 0,
            dedupeMax: 200,
            onClick: (msg) => {
              // Optional: open notifications page
            },
          });
          detachNotifRef.current = detach;
        }
      }
    } else {
      try { detachNotifRef.current?.(); } catch {}
      disconnectWebSocket();
      wsConnected.current = false;
      wsRef.current = null;
    }

    return () => {
      try { detachNotifRef.current?.(); } catch {}
      disconnectWebSocket();
      wsConnected.current = false;
      wsRef.current = null;
    };
  }, [user, token, companyId]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen bg-background text-sm sm:text-base">{t('common.loading')}</div>;
  }

  return (
    <Router>
      <Routes>
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/failure" element={<PaymentFailure />} />
        <Route path="/billing/expired" element={<SubscriptionExpired />} />
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/support" element={<CustomerSupport />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/enquiry" element={<PaymentPage />} />
        </Route>
        <Route path="/company-selection" element={<CompanySelection />} />
        <Route
          path="/login"
          element={<SignInWithCompanyId setUser={setUser} setToken={setToken} setRefreshToken={setRefreshToken} setIsAuthenticated={setIsAuthenticated} setCompanyId={setCompanyId} />}
        />
        <Route
          path="/admin"
          element={user && hasRouteAuth && user.id && user.role === 'admin' ? <AdminDashboard user={user} showTrashAgent={false} dashboardLabel="Admin Dashboard" /> : <Navigate to={`/company-selection?companyId=${companyId || localStorage.getItem('company_id') || '1'}`} />}
        />
        <Route
          path="/admin/create-project"
          element={user && hasRouteAuth && user.id && user.role === 'admin' ? <CreateProject user={user} /> : <Navigate to={`/company-selection?companyId=${companyId || localStorage.getItem('company_id') || '1'}`} />}
        />
        <Route
          path="/admin/create-user"
          element={user && hasRouteAuth && user.id && user.role === 'admin' ? <CreateUser user={user} /> : <Navigate to={`/company-selection?companyId=${companyId || localStorage.getItem('company_id') || '1'}`} />}
        />
        <Route
          path="/manager/*"
          element={
            user && hasRouteAuth && user.id && user.role === 'manager' ? (
              <ManagerView user={user} setUser={setUser} />
            ) : (
              <Navigate to={`/company-selection?companyId=${companyId || localStorage.getItem('company_id') || '1'}`} />
            )
          }
        />
        <Route
          path="/team-member/*"
          element={
            user && hasRouteAuth && user.id && user.role === 'team_member' ? (
              <TeamMemberView user={user} setUser={setUser} />
            ) : (
              <Navigate to={`/company-selection?companyId=${companyId || localStorage.getItem('company_id') || '1'}`} />
            )
          }
        />
        <Route path="*" element={<Navigate to={`/company-selection?companyId=${companyId || localStorage.getItem('company_id') || '1'}`} />} />
      </Routes>
    </Router>
  );
};

export default App;
